import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { Colors } from 'styles';
import { rootTheme } from 'theme/rootTheme';

const Icon = styled.div`
  display: flex;
  align-items: center;
  color: ${Colors.gray7};
  margin-right: 5px;
`;

const BACKGROUND_COLOR = {
  light: Colors.gray4,
  dark: Colors.accountInputBackground,
} as const;

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: React.ReactNode;
  customPrefix?: React.ReactNode;
  customSuffix?: React.ReactNode;
  className?: string;
  fullWidth?: boolean;
  borderColor?: string;
  variant?: 'light' | 'dark';
  inputRef?: React.Ref<HTMLInputElement>;
  ignore1p?: boolean;
}

const WrappedInput = forwardRef<HTMLInputElement, InputProps>(function ForwardedWrappedInput(
  {
    icon,
    className,
    customPrefix,
    variant,
    fullWidth,
    borderColor,
    customSuffix,
    inputRef,
    ignore1p,
    ...props
  }: InputProps,
  ref
) {
  return (
    <div ref={ref} className={className}>
      {icon != null && <Icon>{icon}</Icon>}
      {customPrefix}
      <input {...props} ref={inputRef} data-1p-ignore={ignore1p || undefined} />
      {customSuffix}
    </div>
  );
});

const Input = styled(WrappedInput)<InputProps>`
  display: inline-flex;
  align-items: center;
  border-radius: ${rootTheme.radius.xs}px;
  height: 3.5rem;
  background-color: ${(props: InputProps) => BACKGROUND_COLOR[props.variant || 'light']};
  color: ${Colors.gray9};
  border: ${(props: InputProps) =>
    props.borderColor != null ? `1px solid ${props.borderColor}` : `1px solid ${Colors.gray6}`};
  padding: 0 15px;
  width: ${(props: InputProps) => (props.fullWidth === true ? '100%' : '')};
  outline: 0;

  &:focus-within {
    border-color: ${Colors.blue3};
  }

  input {
    all: unset;
    align-self: stretch;
    flex: 1;
    display: inline-block;

    &::placeholder {
      color: ${Colors.gray8};
    }
  }
`;

export const SearchInput = styled(Input)`
  padding: 1px 6px;
  height: unset;
  background-color: ${Colors.gray2};

  svg {
    height: 15px;
    width: 15px;
  }
`;

export default Input;
